/**
 * Formateo de valores para tablas, tarjetas y gráficos.
 *
 * Convención de locale: es-PE. Miles con coma no, en Perú el separador de
 * miles es la coma y el decimal el punto. `Intl` con es-PE lo hace solo;
 * centralizarlo aquí evita que cada átomo invente su propio formato.
 */

/** Moneda por defecto del curso (UPC, soles). Cambiar aquí y cambia toda la UI. */
export const DEFAULT_CURRENCY = "S/";

const numberFormatter = new Intl.NumberFormat("es-PE", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const decimalFormatter = new Intl.NumberFormat("es-PE", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const ratioFormatter = new Intl.NumberFormat("es-PE", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 3,
});

const compactFormatter = new Intl.NumberFormat("es-PE", {
  notation: "compact",
  maximumFractionDigits: 1,
});

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

/** Convierte lo que venga en la celda a número, o `null` si no es numérico. */
export function toNumber(value: unknown): number | null {
  if (isFiniteNumber(value)) return value;
  if (typeof value === "string") {
    const cleaned = value.trim().replace(/\s/g, "").replace(/,/g, "");
    if (cleaned === "" || cleaned === "-") return null;
    const parsed = Number(cleaned.replace(/^\((.*)\)$/, "-$1"));
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

export function formatNumber(value: number): string {
  return numberFormatter.format(value);
}

export function formatDecimal(value: number): string {
  return decimalFormatter.format(value);
}

export function formatRatio(value: number): string {
  return ratioFormatter.format(value);
}

export function formatCompact(value: number): string {
  if (Math.abs(value) >= 100_000) return compactFormatter.format(value);
  return numberFormatter.format(value);
}

/**
 * Porcentajes. El contrato no fija la unidad de `percent`, así que la escala
 * se decide por columna (ver `inferPercentScale`): valores dentro de ±1 se
 * leen como fracción (0.185 → 18,5 %) y por encima como puntos porcentuales.
 *
 * Dos decimales siempre: en una tasa de descuento, 25.02 % y 25.00 % son
 * decisiones distintas, y redondear a un decimal las esconde.
 */
export function formatPercent(value: number, scale: PercentScale = "fraction"): string {
  const points = scale === "fraction" ? value * 100 : value;
  return `${percentFormatter.format(points)} %`;
}

const percentFormatter = new Intl.NumberFormat("es-PE", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const wholePercentFormatter = new Intl.NumberFormat("es-PE", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

/**
 * Porcentaje sin decimales, para proporciones que se leen de un vistazo: un
 * avance, una tasa de acierto. En esos sitios «85 %» comunica y «85.00 %» solo
 * añade ruido.
 */
export function formatPercentWhole(value: number, scale: PercentScale = "fraction"): string {
  const points = scale === "fraction" ? value * 100 : value;
  return `${wholePercentFormatter.format(points)} %`;
}

export type PercentScale = "fraction" | "points";

/** Decide la escala de una columna de porcentajes mirando todos sus valores. */
export function inferPercentScale(values: unknown[]): PercentScale {
  const numbers = values.map(toNumber).filter((value): value is number => value !== null);
  if (numbers.length === 0) return "fraction";
  const max = Math.max(...numbers.map((value) => Math.abs(value)));
  return max <= 1 ? "fraction" : "points";
}

export function formatCurrency(value: number, currency: string = DEFAULT_CURRENCY): string {
  const formatted = decimalFormatter.format(Math.abs(value));
  const sign = value < 0 ? "-" : "";
  return `${sign}${currency} ${formatted}`;
}

/**
 * Convención contable: los negativos van entre paréntesis en los estados
 * financieros, que es como se leen en clase.
 */
export function formatAccounting(
  value: number,
  currency: string | null = null,
  options: { decimals?: number; signed?: boolean } = {},
): string {
  const decimals = options.decimals ?? 0;
  const formatter = new Intl.NumberFormat("es-PE", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  const absolute = formatter.format(Math.abs(value));
  const prefix = currency ? `${currency} ` : "";
  if (value < 0) return `(${prefix}${absolute})`;
  if (options.signed && value > 0) return `+${prefix}${absolute}`;
  return `${prefix}${absolute}`;
}

/** Formatea una celda cruda según el `format` declarado por la columna. */
export function formatCell(
  value: unknown,
  format: string | null | undefined,
  percentScale: PercentScale = "fraction",
): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Sí" : "No";

  switch (format) {
    case "number": {
      const parsed = toNumber(value);
      return parsed === null ? String(value) : formatNumber(parsed);
    }
    case "currency": {
      const parsed = toNumber(value);
      return parsed === null ? String(value) : formatCurrency(parsed);
    }
    case "percent": {
      const parsed = toNumber(value);
      return parsed === null ? String(value) : formatPercent(parsed, percentScale);
    }
    case "ratio": {
      const parsed = toNumber(value);
      return parsed === null ? String(value) : formatRatio(parsed);
    }
    default:
      return String(value);
  }
}

/** Fechas del protocolo (ISO 8601) en formato corto local. */
export function formatTime(iso: string | null | undefined): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("es-PE", { hour: "2-digit", minute: "2-digit" }).format(date);
}
